import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

type MultiSelectOption = {
  label: string;
  value: string;
  isSelected?: boolean;
};

interface LabeledMultiSelectProps {
  label: string;
  inputId: string;
  selectAllLabel: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/** What the trigger reads when it is not being typed into. */
function resolveTriggerLabel({
  isPlaceholder,
  allSelected,
  selectAllLabel,
  displayedOptions,
}: {
  isPlaceholder: boolean;
  allSelected: boolean;
  selectAllLabel: string;
  displayedOptions: MultiSelectOption[];
}): string {
  if (isPlaceholder) return 'Select...';
  if (allSelected) return selectAllLabel;
  if (displayedOptions.length === 1) return displayedOptions[0]?.label ?? '';
  return `(${displayedOptions.length}) selected`;
}

/** Handles the "cleared but not committed" local state for the multiselect. */
export function LabeledMultiSelect({
  label,
  inputId,
  selectAllLabel,
  options,
  selectedIds,
  onChange,
}: LabeledMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  // The trigger and the typeahead are one element: closed it displays the
  // selection label, open it holds the search query.
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxRefs = useRef<Array<HTMLInputElement | null>>([]);

  // null = use URL-derived value; [] = user explicitly cleared
  const [localSelection, setLocalSelection] = useState<
    MultiSelectOption[] | null
  >(null);

  // Reset local override when URL-derived selection changes externally
  useEffect(() => {
    setLocalSelection(null);
  }, [selectedIds]);

  // A stale query would silently filter the list on the next open
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Focus the search field when the dropdown opens
  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const displayedOptions = useMemo(() => {
    if (localSelection !== null) {
      return localSelection;
    }
    if (selectedIds.length === 0) {
      return options;
    }
    return options.filter((o) => selectedIds.includes(o.value));
  }, [localSelection, selectedIds, options]);

  // Options visible in the dropdown after applying the typeahead filter
  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  // The toggle-all row is hidden while searching, so it only occupies keyboard
  // index 0 when it is actually rendered — every option shifts accordingly.
  const optionIndexOffset = isSearching ? 0 : 1;
  const lastCheckboxIndex = filteredOptions.length - 1 + optionIndexOffset;

  const handleChange = useCallback(
    (newOptions: MultiSelectOption[]) => {
      if (newOptions.length === 0) {
        setLocalSelection([]);
      } else {
        setLocalSelection(null);
        const allSelected = newOptions.length === options.length;
        onChange(allSelected ? [] : newOptions.map((o) => o.value));
      }
    },
    [options, onChange]
  );

  const setCheckboxRef = useCallback(
    (index: number) => (node: HTMLInputElement | null) => {
      checkboxRefs.current[index] = node;
    },
    []
  );

  const focusCheckbox = useCallback((index: number) => {
    checkboxRefs.current[index]?.focus();
  }, []);

  const clearKeyboardFocus = useCallback(() => {
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement &&
      checkboxRefs.current.includes(active)
    ) {
      active.blur();
    }
  }, []);

  const openPopover = useCallback(() => {
    setIsOpen(true);
    // Focus handled by the isOpen effect above
  }, []);

  const commitPendingEmptySelection = useCallback(() => {
    if (localSelection !== null && localSelection.length === 0) {
      setLocalSelection(null);
      onChange([]);
    }
  }, [localSelection, onChange]);

  const closePopover = useCallback(
    (returnFocus = false) => {
      setIsOpen(false);
      commitPendingEmptySelection();
      if (returnFocus) {
        inputRef.current?.focus();
      }
    },
    [commitPendingEmptySelection]
  );

  /** Caret toggle — the field itself only ever opens. */
  function handleToggleClick() {
    if (isOpen) {
      commitPendingEmptySelection();
      setIsOpen(false);
    } else {
      openPopover();
    }
  }

  /**
   * The trigger *is* the search field, so one handler covers both roles: keys
   * that open the popover, and keys that step out of the query into the list.
   */
  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const opensPopover =
      e.key === 'ArrowDown' ||
      e.key === 'ArrowUp' ||
      e.key === 'Enter' ||
      e.key === ' ';

    // Closed, the field is read-only, so these keys open it rather than typing
    if (!isOpen && opensPopover) {
      e.preventDefault();
      openPopover();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusCheckbox(0);
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusCheckbox(lastCheckboxIndex);
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closePopover(true);
    }

    if (e.key === 'Tab') {
      closePopover();
    }
  }

  function handleCheckboxKeyDown(
    e: KeyboardEvent<HTMLInputElement>,
    index: number,
    onToggle: () => void
  ) {
    // Both ends of the list return to the search field, otherwise it would be
    // unreachable by keyboard once focus is in the options.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (index === lastCheckboxIndex) {
        inputRef.current?.focus();
      } else {
        focusCheckbox(index + 1);
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        inputRef.current?.focus();
      } else {
        focusCheckbox(index - 1);
      }
    }

    if (e.key === 'Home') {
      e.preventDefault();
      focusCheckbox(0);
    }

    if (e.key === 'End') {
      e.preventDefault();
      focusCheckbox(lastCheckboxIndex);
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closePopover(true);
    }

    if (e.key === 'Tab') {
      closePopover();
    }
  }

  // Close popover on outside click; commit a pending empty selection on close
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        commitPendingEmptySelection();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, commitPendingEmptySelection]);

  const selectedValues = useMemo(
    () => new Set(displayedOptions.map((o) => o.value)),
    [displayedOptions]
  );

  const allSelected = selectedValues.size === options.length;

  const isPlaceholder = localSelection !== null && localSelection.length === 0;

  /**
   * Whether the trigger acts as a search field rather than a label. Focus is
   * enough — reaching it by Tab has to clear the label and accept typing, the
   * same way clicking into it does, or Backspace and the first keystroke are
   * swallowed by `readOnly`.
   */
  const isEditing = isOpen || isFocused;

  const triggerLabel = resolveTriggerLabel({
    isPlaceholder,
    allSelected,
    selectAllLabel,
    displayedOptions,
  });

  function toggleOption(option: MultiSelectOption) {
    const isSelected = selectedValues.has(option.value);
    const next = isSelected
      ? displayedOptions.filter((o) => o.value !== option.value)
      : [...displayedOptions, option];
    handleChange(next);
  }

  function toggleAll() {
    if (allSelected) {
      handleChange([]);
    } else {
      handleChange(options);
    }
  }

  return (
    <div className="inline-flex w-full flex-col items-start">
      <label htmlFor={inputId} className="flex text-sm font-semibold">
        {label}
      </label>
      <div ref={containerRef} className="relative w-full">
        {/* The trigger doubles as the typeahead: it shows the selection label
            until it is opened, then takes the query with that label as its
            placeholder. */}
        <div className="flex w-full min-w-0 items-center gap-2 rounded-sm border border-neutral-400 bg-brand-white p-3 text-base font-normal leading-normal text-neutral-1100 focus-within:border-neutral-1100 hover:border-neutral-1100">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-controls={isOpen ? `${inputId}-listbox` : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            // Idle, the field stands in for the old static label
            readOnly={!isEditing}
            value={isEditing ? searchQuery : triggerLabel}
            placeholder={isEditing ? triggerLabel : undefined}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onClick={openPopover}
            // Typing is what opens the list — reaching the field by Tab alone
            // should not pop it open while someone is passing through.
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsOpen(true);
            }}
            onKeyDown={handleInputKeyDown}
            className="min-w-0 flex-1 cursor-text truncate bg-transparent outline-none placeholder:text-[#6D6D6D] read-only:cursor-pointer"
            style={
              !isEditing && isPlaceholder ? { color: '#6D6D6D' } : undefined
            }
          />
          <button
            type="button"
            tabIndex={-1}
            aria-label={
              isOpen ? `Hide ${label} options` : `Show ${label} options`
            }
            onClick={handleToggleClick}
            className="flex size-4 shrink-0 items-center justify-center"
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="currentColor"
            >
              <path d="M8.67235 11.7071C8.30045 12.0976 7.69649 12.0976 7.32459 11.7071L1.61222 5.70826C1.24032 5.31771 1.24032 4.68346 1.61222 4.29291C1.98412 3.90236 2.58808 3.90236 2.95998 4.29291L7.99996 9.58563L13.0399 4.29604C13.4118 3.90549 14.0158 3.90549 14.3877 4.29604C14.7596 4.68658 14.7596 5.32084 14.3877 5.71138L8.67533 11.7102L8.67235 11.7071Z" />
            </svg>
          </button>
        </div>

        {isOpen && (
          <div
            id={`${inputId}-listbox`}
            role="group"
            aria-label={label}
            className="absolute inset-x-0 top-full z-10 mt-1 max-h-[328px] overflow-auto rounded-sm border border-neutral-200 bg-brand-white py-2 font-sans text-brand-black shadow-sm"
          >
            {/* Toggling everything at once is meaningless against a filtered
                list, so this row steps aside while searching. */}
            {!isSearching && (
              <label
                htmlFor={`${inputId}-all`}
                onMouseEnter={clearKeyboardFocus}
                className="relative flex cursor-pointer items-center bg-brand-white py-2 pl-10 pr-4 leading-normal focus-within:bg-neutral-100 hover:bg-neutral-100"
              >
                <input
                  id={`${inputId}-all`}
                  ref={setCheckboxRef(0)}
                  type="checkbox"
                  className="absolute left-4 accent-black"
                  checked={allSelected}
                  onChange={toggleAll}
                  onKeyDown={(e) => handleCheckboxKeyDown(e, 0, toggleAll)}
                />
                <span className="block truncate">
                  {allSelected ? 'Deselect all' : 'Select all'}
                </span>
              </label>
            )}

            {filteredOptions.length === 0 && (
              <p className="px-4 py-2 text-sm text-neutral-1300">
                No options found
              </p>
            )}

            {filteredOptions.map((option, index) => {
              const isSelected = selectedValues.has(option.value);
              const optionId = `${inputId}-${option.value}`;
              // Options start at 0 when the toggle-all row is hidden
              const checkboxIndex = index + optionIndexOffset;
              return (
                <label
                  key={option.value}
                  htmlFor={optionId}
                  onMouseEnter={clearKeyboardFocus}
                  className="relative flex cursor-pointer items-center bg-brand-white py-2 pl-10 pr-4 leading-normal focus-within:bg-neutral-100 hover:bg-neutral-100"
                >
                  <input
                    id={optionId}
                    ref={setCheckboxRef(checkboxIndex)}
                    type="checkbox"
                    className="absolute left-4 accent-black"
                    checked={isSelected}
                    onChange={() => toggleOption(option)}
                    onKeyDown={(e) =>
                      handleCheckboxKeyDown(e, checkboxIndex, () =>
                        toggleOption(option)
                      )
                    }
                  />
                  <span className="block truncate">{option.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
